import { observeOn } from 'rxjs/operators';
import { Observable, queueScheduler, observable, from } from 'rxjs';
import { InjectionToken, Injectable, Optional, Inject, NgZone, Pipe, NgModule } from '@angular/core';
import { __assign, __extends } from 'tslib';
import { ApolloClient } from 'apollo-client';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T
 * @param {?} promiseFn
 * @return {?}
 */
function fromPromise(promiseFn) {
    return new Observable(function (subscriber) {
        promiseFn().then(function (result) {
            if (!subscriber.closed) {
                subscriber.next(result);
                subscriber.complete();
            }
        }, function (error) {
            if (!subscriber.closed) {
                subscriber.error(error);
            }
        });
        return function () { return subscriber.unsubscribe(); };
    });
}
var ZoneScheduler = /** @class */ (function () {
    function ZoneScheduler(zone) {
        this.zone = zone;
        this.now = Date.now ? Date.now : function () { return +new Date(); };
    }
    /**
     * @template T
     * @param {?} work
     * @param {?=} delay
     * @param {?=} state
     * @return {?}
     */
    ZoneScheduler.prototype.schedule = /**
     * @template T
     * @param {?} work
     * @param {?=} delay
     * @param {?=} state
     * @return {?}
     */
    function (work, delay, state) {
        if (delay === void 0) { delay = 0; }
        return /** @type {?} */ (this.zone.run(function () {
            return queueScheduler.schedule(work, delay, state);
        }));
    };
    return ZoneScheduler;
}());
/**
 * @template T
 * @param {?} obs
 * @return {?}
 */
function fixObservable(obs) {
    (/** @type {?} */ (obs))[observable] = function () { return obs; };
    return obs;
}
/**
 * @template T
 * @param {?} obs
 * @param {?} ngZone
 * @return {?}
 */
function wrapWithZone(obs, ngZone) {
    return obs.pipe(observeOn(new ZoneScheduler(ngZone)));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var  /**
 * @template T, V
 */
QueryRef = /** @class */ (function () {
    function QueryRef(obsQuery, ngZone) {
        this.obsQuery = obsQuery;
        this.valueChanges = wrapWithZone(from(fixObservable(this.obsQuery)), ngZone);
        this.queryId = this.obsQuery.queryId;
    }
    /**
     * @return {?}
     */
    QueryRef.prototype.result = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.result();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.currentResult = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.currentResult();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.getLastResult = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.getLastResult();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.getLastError = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.getLastError();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.resetLastResults = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.resetLastResults();
    };
    /**
     * @param {?=} variables
     * @return {?}
     */
    QueryRef.prototype.refetch = /**
     * @param {?=} variables
     * @return {?}
     */
    function (variables) {
        return this.obsQuery.refetch(variables);
    };
    /**
     * @template K
     * @param {?} fetchMoreOptions
     * @return {?}
     */
    QueryRef.prototype.fetchMore = /**
     * @template K
     * @param {?} fetchMoreOptions
     * @return {?}
     */
    function (fetchMoreOptions) {
        return this.obsQuery.fetchMore(fetchMoreOptions);
    };
    /**
     * @param {?} options
     * @return {?}
     */
    QueryRef.prototype.subscribeToMore = /**
     * @param {?} options
     * @return {?}
     */
    function (options) {
        return this.obsQuery.subscribeToMore(options);
    };
    /**
     * @param {?} mapFn
     * @return {?}
     */
    QueryRef.prototype.updateQuery = /**
     * @param {?} mapFn
     * @return {?}
     */
    function (mapFn) {
        return this.obsQuery.updateQuery(mapFn);
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.stopPolling = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.stopPolling();
    };
    /**
     * @param {?} pollInterval
     * @return {?}
     */
    QueryRef.prototype.startPolling = /**
     * @param {?} pollInterval
     * @return {?}
     */
    function (pollInterval) {
        return this.obsQuery.startPolling(pollInterval);
    };
    /**
     * @param {?} opts
     * @return {?}
     */
    QueryRef.prototype.setOptions = /**
     * @param {?} opts
     * @return {?}
     */
    function (opts) {
        return this.obsQuery.setOptions(opts);
    };
    /**
     * @param {?} variables
     * @param {?=} tryFetch
     * @param {?=} fetchResults
     * @return {?}
     */
    QueryRef.prototype.setVariables = /**
     * @param {?} variables
     * @param {?=} tryFetch
     * @param {?=} fetchResults
     * @return {?}
     */
    function (variables, tryFetch, fetchResults) {
        if (tryFetch === void 0) { tryFetch = false; }
        if (fetchResults === void 0) { fetchResults = true; }
        return this.obsQuery.setVariables(variables, tryFetch, fetchResults);
    };
    return QueryRef;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var APOLLO_OPTIONS = new InjectionToken('[apollo-angular] options');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template TCacheShape
 */
var  /**
 * @template TCacheShape
 */
ApolloBase = /** @class */ (function () {
    function ApolloBase(ngZone, _client) {
        this.ngZone = ngZone;
        this._client = _client;
    }
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.watchQuery = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        return new QueryRef(this.client.watchQuery(__assign({}, options)), this.ngZone);
    };
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.query = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        var _this = this;
        return fromPromise(function () {
            return _this.client.query(__assign({}, options));
        });
    };
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.mutate = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        var _this = this;
        return fromPromise(function () {
            return _this.client.mutate(__assign({}, options));
        });
    };
    /**
     * @param {?} options
     * @param {?=} extra
     * @return {?}
     */
    ApolloBase.prototype.subscribe = /**
     * @param {?} options
     * @param {?=} extra
     * @return {?}
     */
    function (options, extra) {
        /** @type {?} */
        var obs = from(fixObservable(this.client.subscribe(__assign({}, options))));
        return extra && extra.useZone !== true
            ? obs
            : wrapWithZone(obs, this.ngZone);
    };
    /**
     * @return {?}
     */
    ApolloBase.prototype.getClient = /**
     * @return {?}
     */
    function () {
        return this._client;
    };
    /**
     * @param {?} client
     * @return {?}
     */
    ApolloBase.prototype.setClient = /**
     * @param {?} client
     * @return {?}
     */
    function (client) {
        if (this._client) {
            throw new Error('Client has been already defined');
        }
        this._client = client;
    };
    Object.defineProperty(ApolloBase.prototype, "client", {
        get: /**
         * @return {?}
         */
        function () {
            this.beforeEach();
            return this._client;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    ApolloBase.prototype.beforeEach = /**
     * @return {?}
     */
    function () {
        this.checkInstance();
    };
    /**
     * @return {?}
     */
    ApolloBase.prototype.checkInstance = /**
     * @return {?}
     */
    function () {
        if (!this._client) {
            throw new Error('Client has not been defined yet');
        }
    };
    return ApolloBase;
}());
var Apollo = /** @class */ (function (_super) {
    __extends(Apollo, _super);
    function Apollo(_ngZone, apolloOptions) {
        var _this = _super.call(this, _ngZone) || this;
        _this._ngZone = _ngZone;
        _this.map = new Map();
        if (apolloOptions) {
            _this.createDefault(apolloOptions);
        }
        return _this;
    }
    /**
     * @template TCacheShape
     * @param {?} options
     * @param {?=} name
     * @return {?}
     */
    Apollo.prototype.create = /**
     * @template TCacheShape
     * @param {?} options
     * @param {?=} name
     * @return {?}
     */
    function (options, name) {
        if (name && name !== 'default') {
            this.createNamed(name, options);
        }
        else {
            this.createDefault(options);
        }
    };
    /**
     * @return {?}
     */
    Apollo.prototype.default = /**
     * @return {?}
     */
    function () {
        return this;
    };
    /**
     * @param {?} name
     * @return {?}
     */
    Apollo.prototype.use = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        if (name === 'default') {
            return this.default();
        }
        return this.map.get(name);
    };
    /**
     * @template TCacheShape
     * @param {?} options
     * @return {?}
     */
    Apollo.prototype.createDefault = /**
     * @template TCacheShape
     * @param {?} options
     * @return {?}
     */
    function (options) {
        if (this.getClient()) {
            throw new Error('Apollo has been already created.');
        }
        return this.setClient(new ApolloClient(options));
    };
    /**
     * @template TCacheShape
     * @param {?} name
     * @param {?} options
     * @return {?}
     */
    Apollo.prototype.createNamed = /**
     * @template TCacheShape
     * @param {?} name
     * @param {?} options
     * @return {?}
     */
    function (name, options) {
        if (this.map.has(name)) {
            throw new Error("Client " + name + " has been already created");
        }
        this.map.set(name, new ApolloBase(this._ngZone, new ApolloClient(options)));
    };
    Apollo.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Apollo.ctorParameters = function () { return [
        { type: NgZone },
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [APOLLO_OPTIONS,] }] }
    ]; };
    return Apollo;
}(ApolloBase));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var Query = /** @class */ (function () {
    function Query(apollo) {
        this.apollo = apollo;
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Query.prototype.watch = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.watchQuery(__assign({}, options, { variables: variables, query: this.document }));
    };
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Query.prototype.fetch = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.query(__assign({}, options, { variables: variables, query: this.document }));
    };
    Query.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Query.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Query;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var Mutation = /** @class */ (function () {
    function Mutation(apollo) {
        this.apollo = apollo;
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Mutation.prototype.mutate = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.mutate(__assign({}, options, { variables: variables, mutation: this.document }));
    };
    Mutation.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Mutation.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Mutation;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var Subscription = /** @class */ (function () {
    function Subscription(apollo) {
        this.apollo = apollo;
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @param {?=} extra
     * @return {?}
     */
    Subscription.prototype.subscribe = /**
     * @param {?=} variables
     * @param {?=} options
     * @param {?=} extra
     * @return {?}
     */
    function (variables, options, extra) {
        return this.apollo.subscribe(__assign({}, options, { variables: variables, query: this.document }), extra);
    };
    Subscription.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Subscription.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Subscription;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var SelectPipe = /** @class */ (function () {
    function SelectPipe() {
    }
    /**
     * @param {?} obj
     * @param {?=} name
     * @return {?}
     */
    SelectPipe.prototype.transform = /**
     * @param {?} obj
     * @param {?=} name
     * @return {?}
     */
    function (obj, name) {
        if (name === void 0) { name = ''; }
        if (name !== '') {
            return obj && obj.data && obj.data[name];
        }
    };
    SelectPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'select',
                },] }
    ];
    return SelectPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var PROVIDERS = [Apollo];
/** @type {?} */
var DECLARATIONS = [SelectPipe];
var ApolloModule = /** @class */ (function () {
    function ApolloModule() {
    }
    ApolloModule.decorators = [
        { type: NgModule, args: [{
                    providers: PROVIDERS,
                    declarations: DECLARATIONS,
                    exports: DECLARATIONS,
                },] }
    ];
    return ApolloModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { Apollo, ApolloBase, QueryRef, Query, Mutation, Subscription, SelectPipe, ApolloModule, APOLLO_OPTIONS, DECLARATIONS as ɵb, PROVIDERS as ɵa };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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